import { TEDCApplicant, TQuestionWithAnswer, getValidationResult, useEDCFlow } from "@smvtech/x-flux";
import DocumentPreview from "../DocumentPreview";
import DocumentUpload from "../DocumentUpload";
import AnalysingDocumentOverlay from "./AnalysingDocumentOverlay";
import AnalysisProgress from "./AnalysisProgress";
import DocumentVerificationStatus from "./DocumentVerificationStatus";
import Overlay from "../DocumentPreview/PDFRenderer/Overlay";
import PDFViewer from "../DocumentPreview/PDFRenderer/PDFViewer";
import { useDocumentItem } from "../../../../hooks/useDocumentItem";
import { useMemo } from "react";
import { Tooltip } from "antd";

type DocumentListItemProps = {
	document: TQuestionWithAnswer;
	activeApplicantData: TEDCApplicant;
	questions: TQuestionWithAnswer[];
	isErrorMode?: boolean;
};
const DocumentListItem = ({ document, activeApplicantData, questions, isErrorMode = false }: DocumentListItemProps) => {
	const { order } = useEDCFlow();

	const isDocumentLocked = !!order?.qc_terms_and_conditions;
	const validationResult = useMemo(() => {
		return getValidationResult(document);
	}, [document]);

	const {
		signedUrl,
		isLoading,
		uploading,
		hasDocument,
		isPdf,
		reUploadInputId,
		timeSaved,
		showModalWithSignedURL,
		setShowModalWithSignedURL,
		handleUploadStart,
		handleUploadProgress,
		handleUploadComplete,
		handleReUpload,
		handlePreviewClick,
		isUnfixable
	} = useDocumentItem({
		document,
		activeApplicantData,
		questions,
		validationResult
	});

	return (
		<>
			<div
				className={hasDocument && isUnfixable ? "rounded-lg border-[1px] p-4 border-red-600 bg-[#FEF3F2]" : ""}>
				<div className="grid grid-cols-2 gap-6">
					<div className="flex flex-col gap-2">
						<div
							className={`aspect-[5/4] w-full overflow-hidden rounded-lg border-[1px] border-dashed ${hasDocument && isUnfixable ? "border-red-600" : "border-[#B3B3B3]/60"} bg-[#F9FAFA]/70`}>
							{hasDocument ? (
								<div className="w-full h-full p-2">
									<DocumentPreview
										signedUrl={signedUrl!}
										isPdf={isPdf}
										alt={document.question.question}
										onPreviewClick={handlePreviewClick}
										isLoading={isLoading}
										onImageHover={() => {
											if (!isPdf) {
												// for future use
											}
										}}
									/>
								</div>
							) : uploading.isUploading ? (
								<AnalysingDocumentOverlay />
							) : (
								<DocumentUpload
									document={document}
									activeApplicantData={activeApplicantData}
									onUploadStart={handleUploadStart}
									onUploadProgress={handleUploadProgress}
									onUploadComplete={handleUploadComplete}
								/>
							)}
						</div>
						{hasDocument && (
							<div className="flex items-center justify-center text-center">
								<input
									type="file"
									accept="image/jpeg,image/png,application/pdf"
									hidden
									id={reUploadInputId}
									onChange={(e) => handleReUpload(e.target.files)}
									disabled={isDocumentLocked}
								/>
								{!isErrorMode && (
									<Tooltip
										styles={{ body: { width: isDocumentLocked ? "200px" : "320px" } }}
										title={isDocumentLocked ? "Order has already been placed." : null}>
										<label
											htmlFor={reUploadInputId}
											className={`text-sm text-[#B3B3B3] underline hover:text-[#A9A9A9] ${
												isDocumentLocked ? "cursor-not-allowed" : "cursor-pointer"
											}`}>
											Re Upload
										</label>
									</Tooltip>
								)}
							</div>
						)}
					</div>
					<div className="flex flex-col gap-2">
						{hasDocument ? (
							<DocumentVerificationStatus
								validationResult={validationResult}
								document={document}
								timeSaved={timeSaved}
								reUploadInputId={reUploadInputId}
								isErrorMode={isErrorMode}
							/>
						) : uploading.isUploading ? (
							<AnalysisProgress isUploading={uploading.isUploading} />
						) : (
							<div className="h-full flex max-w-xs flex-col justify-center gap-2">
								<p className="text-sm text-gray-400">
									Place upload a clear and readable copy of the document.
								</p>
								<p className="text-sm text-gray-400">
									Upload documents in JPEG, PNG, or PDF format within 2MB.
								</p>
							</div>
						)}
					</div>
				</div>
			</div>
			{showModalWithSignedURL && (
				<Overlay open={true} onClose={() => setShowModalWithSignedURL(null)} className="rounded-xl">
					<PDFViewer
						signedURL={showModalWithSignedURL.signedURL}
						className="w-full"
						isPDF={showModalWithSignedURL.isPDF}
					/>
				</Overlay>
			)}
		</>
	);
};

export default DocumentListItem;

